import React, { useState } from 'react'
import { useQuery } from 'react-query'
import { api } from '../lib/api'
import { X, Search, Users, UserPlus, Check } from 'lucide-react'

interface Student {
  id: number
  first_name: string
  last_name: string
  klasse: string
  photo?: string
  foto?: string
}

interface Course {
  id: number
  name: string
  fach: string
  jahrgang: string
  ersteller_name: string
  farbe: string
  icon: string
  is_active: boolean
  teilnehmer_anzahl: number
  max_teilnehmer: number
  created_at: string
}

interface AddStudentModalProps {
  course: Course
  onClose: () => void
  onAddStudents: (studentIds: number[]) => void
}

export default function AddStudentModal({ course, onClose, onAddStudents }: AddStudentModalProps) {
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedStudents, setSelectedStudents] = useState<number[]>([])
  const [klasseFilter, setKlasseFilter] = useState('')

  // Fetch students
  const { data: studentsData, isLoading: studentsLoading } = useQuery(
    'students',
    () => api.get('/courses-groups/students').then(res => res.data)
  )

  const students = studentsData?.data || []

  // Filter students based on search term and class filter
  const filteredStudents = students.filter((student: Student) => {
    if (!student) return false
    
    const search = searchTerm.toLowerCase()
    const matchesSearch = 
      student.first_name.toLowerCase().includes(search) ||
      student.last_name.toLowerCase().includes(search) ||
      student.klasse.toLowerCase().includes(search)
    
    const matchesKlasse = !klasseFilter || student.klasse === klasseFilter
    
    return matchesSearch && matchesKlasse
  })

  // Get unique classes for filter
  const uniqueClasses: string[] = [...new Set(students.map((s: Student) => s.klasse).filter(Boolean) as string[])].sort()

  const toggleStudent = (studentId: number) => {
    setSelectedStudents(prev => 
      prev.includes(studentId) 
        ? prev.filter(id => id !== studentId)
        : [...prev, studentId]
    )
  }

  const handleSelectAll = () => {
    if (selectedStudents.length === filteredStudents.length) {
      setSelectedStudents([])
    } else {
      setSelectedStudents(filteredStudents.map((s: Student) => s.id))
    }
  }

  const handleAddStudents = () => {
    onAddStudents(selectedStudents)
    onClose()
  }

  if (studentsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-4xl mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900">
            Schüler zu "{course.name}" hinzufügen
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Search and Filter */}
        <div className="mb-4 space-y-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Schüler suchen..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                />
              </div>
            </div>
            <div className="sm:w-48">
              <select
                value={klasseFilter}
                onChange={(e) => setKlasseFilter(e.target.value)}
                className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">Alle Klassen ({uniqueClasses.length})</option>
                {uniqueClasses.map((klasse: string) => (
                  <option key={klasse} value={klasse}>Klasse {klasse}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex justify-between items-center">
            <button
              onClick={handleSelectAll}
              className="text-sm text-primary-600 hover:text-primary-500"
            >
              {selectedStudents.length === filteredStudents.length && filteredStudents.length > 0 ? 'Alle abwählen' : 'Alle auswählen'}
            </button>
            <div className="text-sm text-gray-500">
              <span>{selectedStudents.length} von {filteredStudents.length} ausgewählt</span>
              {klasseFilter && (
                <span className="ml-2 text-blue-600">(gefiltert nach Klasse {klasseFilter})</span>
              )}
            </div>
          </div>
        </div>

        {/* Students List */}
        <div className="flex-1 overflow-y-auto">
          <div className="grid grid-cols-1 gap-2">
            {filteredStudents.map((student: Student) => (
              <label
                key={student.id}
                className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={selectedStudents.includes(student.id)}
                  onChange={() => toggleStudent(student.id)}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <div className="ml-3 flex items-center flex-1">
                  <div className="flex-shrink-0">
                    {student.photo || student.foto ? (
                      <img
                        className="h-8 w-8 rounded-full"
                        src={`data:image/jpeg;base64,${student.photo || student.foto}`}
                        alt={`${student.first_name} ${student.last_name}`}
                      />
                    ) : (
                      <div className="h-8 w-8 rounded-full bg-gray-300 flex items-center justify-center">
                        <Users className="h-4 w-4 text-gray-500" />
                      </div>
                    )}
                  </div>
                  <div className="ml-3 flex-1">
                    <div className="text-sm font-medium text-gray-900">
                      {student.first_name} {student.last_name}
                    </div>
                    <div className="text-sm text-gray-500">{student.klasse}</div>
                  </div>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="mt-4 flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Abbrechen
          </button>
          <button
            onClick={handleAddStudents}
            disabled={selectedStudents.length === 0}
            className="px-4 py-2 bg-primary-600 text-white rounded-md text-sm font-medium hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <UserPlus className="h-4 w-4 inline mr-2" />
            {selectedStudents.length} Schüler hinzufügen
          </button>
        </div>
      </div>
    </div>
  )
}



